import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Newspaper,
  FileText,
  Briefcase,
  Users,
  Building2,
  DollarSign,
  Scale,
  Image,
  FolderOpen,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import { newsService } from '../../services/api/newsService';
import { tendersService } from '../../services/api/tendersService';
import { gosService } from '../../services/api/gosService';
import { documentsService } from '../../services/api/documentsService';
import { galleryService } from '../../services/api/galleryService';
import { contactsService } from '../../services/api/contactsService';
import { circlesDivisionsService } from '../../services/api/circlesDivisionsService';
import { steelCementRatesService } from '../../services/api/steelCementRatesService';
import { rtiOfficersService } from '../../services/api/rtiOfficersService';
import { administrationService } from '../../services/api/administrationService';

interface ModuleStat {
  label: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  link: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<ModuleStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const [
        newsRes,
        tendersRes,
        gosRes,
        docsRes,
        galleryRes,
        contactsRes,
        circlesRes,
        ratesRes,
        rtiRes,
        adminRes,
      ] = await Promise.allSettled([
        newsService.getAll(),
        tendersService.getAll(),
        gosService.getAll(),
        documentsService.getAll(),
        galleryService.getAll(),
        contactsService.getAll(),
        circlesDivisionsService.getAll(),
        steelCementRatesService.getAll(),
        rtiOfficersService.getAll(),
        administrationService.getAll(),
      ]);

      const count = (res: PromiseSettledResult<any>) =>
        res.status === 'fulfilled' ? (res.value?.data?.length || 0) : 0;

      const docs = count(docsRes) > 0 && docsRes.status === 'fulfilled' ? docsRes.value.data : [];
      const btCount = docs.filter((d: any) => d.category === 'bt_rates').length;
      const ssrCount = docs.filter((d: any) => d.category === 'ssr').length;
      const ssrEmdCount = docs.filter((d: any) => d.category === 'ssr_emd_tax_rates').length;
      const ssrSorsCount = docs.filter((d: any) => d.category === 'ssr_final_sors').length;
      const achieveCount = docs.filter((d: any) => d.category === 'achievements').length;

      setStats([
        { label: 'News', count: count(newsRes), icon: <Newspaper size={24} />, color: 'blue', link: '/news' },
        { label: 'Government Orders', count: count(gosRes), icon: <BookOpen size={24} />, color: 'indigo', link: '/government-orders' },
        { label: 'Tenders', count: count(tendersRes), icon: <Briefcase size={24} />, color: 'green', link: '/tenders' },
        { label: 'Gallery', count: count(galleryRes), icon: <Image size={24} />, color: 'pink', link: '/gallery' },
        { label: 'BT Rates', count: btCount, icon: <FolderOpen size={24} />, color: 'orange', link: '/documents/bt_rates' },
        { label: 'SSR', count: ssrCount, icon: <FolderOpen size={24} />, color: 'teal', link: '/documents/ssr' },
        { label: 'SSR EMD Tax Rates', count: ssrEmdCount, icon: <FolderOpen size={24} />, color: 'cyan', link: '/documents/ssr_emd_tax_rates' },
        { label: 'SSR Final SORs', count: ssrSorsCount, icon: <FolderOpen size={24} />, color: 'violet', link: '/documents/ssr_final_sors' },
        { label: 'Achievements', count: achieveCount, icon: <FolderOpen size={24} />, color: 'amber', link: '/documents/achievements' },
        { label: 'Steel & Cement Rates', count: count(ratesRes), icon: <DollarSign size={24} />, color: 'yellow', link: '/steel-cement-rates' },
        { label: 'Contacts', count: count(contactsRes), icon: <Users size={24} />, color: 'purple', link: '/contacts' },
        { label: 'Circles & Divisions', count: count(circlesRes), icon: <Building2 size={24} />, color: 'emerald', link: '/circles-divisions' },
        { label: 'Administration', count: count(adminRes), icon: <FileText size={24} />, color: 'slate', link: '/administration' },
        { label: 'RTI Officers', count: count(rtiRes), icon: <Scale size={24} />, color: 'red', link: '/rti-officers' },
      ]);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const colorMap: Record<string, { bg: string; text: string; icon: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'bg-blue-100 text-blue-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'bg-indigo-100 text-indigo-600' },
    green: { bg: 'bg-green-50', text: 'text-green-700', icon: 'bg-green-100 text-green-600' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-700', icon: 'bg-pink-100 text-pink-600' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'bg-orange-100 text-orange-600' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-700', icon: 'bg-teal-100 text-teal-600' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-700', icon: 'bg-cyan-100 text-cyan-600' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'bg-violet-100 text-violet-600' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'bg-amber-100 text-amber-600' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: 'bg-yellow-100 text-yellow-600' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'bg-purple-100 text-purple-600' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'bg-emerald-100 text-emerald-600' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-700', icon: 'bg-slate-100 text-slate-600' },
    red: { bg: 'bg-red-50', text: 'text-red-700', icon: 'bg-red-100 text-red-600' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to PRED Admin CMS</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {stats.map((item) => {
          const colors = colorMap[item.color] || colorMap.blue;
          return (
            <Link
              key={item.label}
              to={item.link}
              className={`${colors.bg} border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow group`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.icon}`}>
                  {item.icon}
                </div>
                <ArrowRight size={16} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{item.count}</h3>
              <p className={`text-sm font-medium ${colors.text} mt-1`}>{item.label}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
